import { useState, useEffect, useCallback, useRef } from 'react';
import { Loader2, SlidersHorizontal, Users } from 'lucide-react';
import { type SegmentDefinition, segmentDefinitionSchema } from '@masters-club/validation';
import { useCustomMutation } from '@refinedev/core';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
  SheetFooter,
} from '@/components/ui/sheet';
import { SegmentBuilder } from './SegmentBuilder';

export interface SegmentFilterSheetProps {
  value: SegmentDefinition;
  onChange: (next: SegmentDefinition) => void;
  disabled?: boolean;
  /**
   * 'live'  — изменения применяются сразу (onChange на каждое действие).
   * 'apply' — изменения копятся в драфте, применяются по кнопке «Применить».
   */
  mode?: 'live' | 'apply';
  /** Текст кнопки-триггера. */
  triggerLabel?: string;
  /** Заголовок панели. */
  title?: string;
  description?: string;
  /** Вызывается при открытии панели (напр. подгрузить сохранённые сегменты). */
  onOpen?: () => void;
  /**
   * Доп. контент в Sheet (напр. сохранённые сегменты). Render-prop даёт
   * доступ к текущему сегменту и сеттеру — чтобы save/load работали
   * с актуальным значением (в режиме apply — с драфтом).
   */
  children?: React.ReactNode | ((args: {
    current: SegmentDefinition;
    setCurrent: (next: SegmentDefinition) => void;
  }) => React.ReactNode);
}

const EMPTY_SEGMENT: SegmentDefinition = { groupsOp: 'AND', groups: [] };

interface PreviewResponse {
  total: number;
  users: unknown[];
}

function isPreviewResponse(v: unknown): v is PreviewResponse {
  return (
    typeof v === 'object' &&
    v !== null &&
    'total' in v &&
    typeof (v as PreviewResponse).total === 'number'
  );
}

/**
 * Переиспользуемый фильтр-сегмент: компактная кнопка с бейджем количества
 * условий открывает боковую панель (Sheet) с SegmentBuilder. Заменяет
 * inline-конструктор, чтобы не занимать вертикальное место на странице.
 */
export function SegmentFilterSheet({
  value,
  onChange,
  disabled = false,
  mode = 'live',
  triggerLabel = 'Условия отбора',
  title = 'Условия отбора получателей',
  description,
  onOpen,
  children,
}: SegmentFilterSheetProps) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<SegmentDefinition>(value);

  // Счётчик аудитории
  const [audienceTotal, setAudienceTotal] = useState<number | null>(null);
  const [audienceLoading, setAudienceLoading] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const requestIdRef = useRef(0);

  const { mutate: fetchPreview } = useCustomMutation<PreviewResponse>();

  const currentSegment = mode === 'apply' ? draft : value;

  // Запрашиваем счётчик при изменении сегмента (с дебаунсом 450ms)
  useEffect(() => {
    if (!open) { return; }

    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    debounceRef.current = setTimeout(() => {
      // Не шлём запрос для черновика с незаполненными условиями
      // (иначе backend 400 → глобальный тост ошибки пугает пользователя).
      // Пустой сегмент (groups: []) валиден — это «вся аудитория».
      const validation = segmentDefinitionSchema.safeParse(currentSegment);
      if (!validation.success) {
        setAudienceTotal(null);
        setAudienceLoading(false);
        return;
      }

      const reqId = ++requestIdRef.current;
      setAudienceLoading(true);

      fetchPreview(
        {
          url: '/audience/preview',
          method: 'post',
          values: { definition: validation.data, limit: 1 },
        },
        {
          onSuccess: (r) => {
            if (reqId !== requestIdRef.current) { return; }
            if (isPreviewResponse(r.data)) {
              setAudienceTotal(r.data.total);
            }
            setAudienceLoading(false);
          },
          onError: () => {
            if (reqId !== requestIdRef.current) { return; }
            setAudienceTotal(null);
            setAudienceLoading(false);
          },
        },
      );
    }, 450);

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentSegment, open]);

  const handleOpenChange = useCallback(
    (next: boolean) => {
      setOpen(next);
      if (next) {
        onOpen?.();
        // Сбрасываем счётчик при открытии
        setAudienceTotal(null);
      }
    },
    [onOpen],
  );

  // При открытии в режиме apply — синхронизируем драфт с актуальным значением.
  useEffect(() => {
    if (open && mode === 'apply') {
      setDraft(value);
    }
  }, [open, mode, value]);

  const conditionsCount = value.groups.reduce(
    (sum, g) => sum + (g.conditions?.length ?? 0),
    0,
  );
  const hasConditions = value.groups.length > 0;

  const handleBuilderChange = useCallback(
    (next: SegmentDefinition) => {
      if (mode === 'live') {
        onChange(next);
      } else {
        setDraft(next);
      }
    },
    [mode, onChange],
  );

  const handleApply = useCallback(() => {
    onChange(draft);
    setOpen(false);
  }, [draft, onChange]);

  const handleClear = useCallback(() => {
    if (mode === 'live') {
      onChange(EMPTY_SEGMENT);
    } else {
      setDraft(EMPTY_SEGMENT);
    }
  }, [mode, onChange]);

  return (
    <Sheet open={open} onOpenChange={handleOpenChange}>
      <Button
        type="button"
        variant={hasConditions ? 'default' : 'outline'}
        size="sm"
        className="gap-2"
        disabled={disabled}
        onClick={() => handleOpenChange(true)}
      >
        <SlidersHorizontal className="h-4 w-4" />
        {triggerLabel}
        {conditionsCount > 0 && (
          <Badge variant="secondary" className="ml-1 px-1.5 py-0 text-xs tabular-nums">
            {conditionsCount}
          </Badge>
        )}
      </Button>

      <SheetContent
        side="right"
        title={title}
        className="w-full gap-0 sm:max-w-2xl lg:max-w-3xl"
      >
        <SheetHeader className="px-0">
          <SheetTitle>{title}</SheetTitle>
          {description && <SheetDescription>{description}</SheetDescription>}
        </SheetHeader>

        <div className="flex-1 overflow-y-auto py-4">
          {typeof children === 'function'
            ? children({
                current: mode === 'apply' ? draft : value,
                setCurrent: handleBuilderChange,
              })
            : children}
          <SegmentBuilder
            value={mode === 'apply' ? draft : value}
            onChange={handleBuilderChange}
            disabled={disabled}
          />
        </div>

        {/* Живой счётчик аудитории */}
        <div className="flex items-center gap-2 py-3 border-t text-sm text-muted-foreground">
          <Users className="h-4 w-4 shrink-0" />
          <AudienceCountLabel loading={audienceLoading} total={audienceTotal} />
        </div>

        <SheetFooter className="flex-row justify-between gap-2 px-0">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={handleClear}
            disabled={disabled || (mode === 'apply' ? draft.groups.length === 0 : !hasConditions)}
          >
            Сбросить
          </Button>
          {mode === 'apply' ? (
            <Button type="button" size="sm" onClick={handleApply} disabled={disabled}>
              Применить
            </Button>
          ) : (
            <Button type="button" size="sm" onClick={() => setOpen(false)}>
              Готово
            </Button>
          )}
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}

interface AudienceCountLabelProps {
  loading: boolean;
  total: number | null;
}

/**
 * Лейбл живого счётчика аудитории: «Подсчёт…» / «Подходит N пользователей» / «—».
 * Вынесен из JSX, чтобы избежать вложенного тернарника в разметке.
 */
function AudienceCountLabel({ loading, total }: AudienceCountLabelProps) {
  if (loading) {
    return (
      <>
        <Loader2 className="h-4 w-4 animate-spin shrink-0" />
        <span>Подсчёт...</span>
      </>
    );
  }
  if (total !== null) {
    return (
      <span>
        Подходит{' '}
        <span className="font-semibold text-foreground">
          {total.toLocaleString('ru-RU')}
        </span>{' '}
        пользователей
      </span>
    );
  }
  return <span>—</span>;
}
